import moment from "moment";
import { TSelectorList } from "./type";

// 根据 filterConfig 简化逻辑
export const getFilterConfig = (
  filters: TSelectorList[],
  filterConfig?: Record<string, { listKey: string; include?: string[]; exclude?: string[]; }>,
  filterChangeParam?: Record<string, any>
): Record<string, string[]> => {
  // 定义默认输出，先设置默认值
  const defaultFilters: Record<string, string[]> = {
    zone_list: ['255'],
    market_list: ['255'],
    channel_list: ['255'],
    os_list: ['255'],
    category_list: ['255'],
    product_list: ['255'],
    lang_list: ['255'],
  };
    // 如果没有 filterConfig，则直接返回原始默认配置
  if (!filterConfig || !filters) {
    return defaultFilters;
  }
  // 遍历 filterConfig
  Object.entries(filterConfig).forEach(([filterKey, { listKey, include, exclude }]) => {
    // 在 filters 中找到对应的 select_key
    const filterItem = filters.find((item) => item.select_key === filterKey);
    if (!filterItem) {
      return; // 如果没有找到对应的 select_key，跳过
    }
    if (include && include.length > 0) {
      // 如果有 include，直接使用 include 的值，忽略 select_list
      defaultFilters[listKey] = include;
    } else if (exclude && exclude.length > 0) {
      // 如果有 exclude，则从 select_list 中排除指定的项
      const { select_list } = filterItem;
      defaultFilters[listKey] = select_list
        .filter((item) => !exclude.includes(item.code))
        .map((item) => item.code);
    }
  });
  // 特殊处理：根据过滤后的 category_list 查找 sub_select 的 code，并加入 product_list
  const categoryItem = filters.find((item) => item.select_key === 'category');
  if (categoryItem) {
    const subSelectCodes: string[] = [];
    // 遍历当前过滤后的 category_list
    if (defaultFilters.category_list.length > 0) {
      categoryItem.select_list.forEach((category) => {
        // 如果当前 category 的 code 在过滤后的 category_list 中
        if (defaultFilters.category_list.includes(category.code) && category.sub_select) {
          subSelectCodes.push(...category.sub_select.map((sub) => sub.code));
        }
      });
      if (subSelectCodes.length === 0) {
        subSelectCodes.push("255");
      }
      // 如果原先 product_list 不为默认值，则合并，否则直接使用 subSelectCodes
      if (defaultFilters.product_list[0] === '255') {
        defaultFilters.product_list = subSelectCodes;
      } else {
        defaultFilters.product_list = [...defaultFilters.product_list, ...subSelectCodes];
      }
    }
  }

  // 最后处理：用 filterChangeParam 覆盖 defaultFilters 中已有的值
  if (filterChangeParam) {
    Object.entries(filterChangeParam).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        defaultFilters[key] = value;
      }
    });
  }
  return defaultFilters;
};

export const extractParamsFromCarryFilter = (
  extraCarryParams: { [x: string]: string; },
  carryFilter: Record<string, any>
) => {
  const extractedParams: Record<string, any> = {};

  Object?.keys(extraCarryParams)?.forEach(key => {
    const carryFilterKey = extraCarryParams?.[key];
    if (carryFilter?.[carryFilterKey] !== undefined) {
      extractedParams[key] = carryFilter[carryFilterKey];
    } else {
      // 如果 carryFilter 中没有对应的 key，就保留 extraCarryParams 的原始 key 和 value
      extractedParams[key] = carryFilterKey;
    }
  });

  return extractedParams;
};

/**
 * 格式化事件数据
 * @param {Object} data - 后端返回的数据
 * @returns {Object} - 格式化后的数据，按日期分组
 */
export function formatEventData(data) {
  const result = {};
  const eventTracker = {}; // 追踪每个事件名称的首次和末次出现

  // 遍历所有日期
  data.list.forEach(item => {
    const formattedDate = moment(item.date, 'YYYYMMDD').format('YYYY-MM-DD');
    
    if (item.event_list && item.event_list.length > 0) {
      item.event_list.forEach(event => {
        const eventName = event.event_name;
        
        // 如果事件名称第一次出现
        if (!eventTracker[eventName]) {
          eventTracker[eventName] = {
            firstDate: formattedDate,
            lastDate: formattedDate
          };
          
          // 初始化该日期的数组
          if (!result[formattedDate]) {
            result[formattedDate] = [];
          }
          
          // 添加事件到结果中
          result[formattedDate].push({
            name: eventName,
            name_cn: eventName,
            start_time: formattedDate,
            end_time: formattedDate
          });
        } else {
          // 更新最后出现的日期和结束时间
          eventTracker[eventName].lastDate = formattedDate;
          
          // 更新第一次出现位置的 end_time
          const firstDate = eventTracker[eventName].firstDate;
          if (result[firstDate]) {
            const eventIndex = result[firstDate].findIndex(e => (e.name || e.name_cn) === eventName);
            if (eventIndex !== -1) {
              result[firstDate][eventIndex].end_time = formattedDate;
            }
          }
          // 后续出现的相同事件名称不添加到结果中
        }
      });
    }
  });

  return result;
}
